import React, { useEffect, useState } from "react";
import {
  adminPayoutsSettings,
  adminTogglePayouts,
  adminSetUserSplit,
  adminListWithdrawals,
  adminDecideWithdrawal,
} from "../../lib/api";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";

/**
 * PayoutsSection — admin home for the creator-payouts feature.
 *
 * Three responsibilities:
 *   1. Global on/off switch for tipping + the wallet surface.
 *      Flipping this off instantly hides the Tip button + Wallet page
 *      across the whole app.
 *   2. Per-user platform-fee % override. Default is 10% (player keeps
 *      90% of every tip); admin can drop to 0 or raise to 50.
 *   3. Withdrawal queue. Players submit PayPal / IBAN / Crypto
 *      destinations + amount; admin moves them through
 *      requested → approved → paid (or rejected → refunded).
 */
export function PayoutsSection({ onToast }) {
  const { refresh } = useFeatureFlags();
  const [settings, setSettings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [statusFilter, setStatusFilter] = useState("requested");
  const [splitUid, setSplitUid] = useState("");
  const [splitPct, setSplitPct] = useState("10");
  const [busy, setBusy] = useState("");

  const load = React.useCallback(async () => {
    try {
      const s = await adminPayoutsSettings();
      setSettings(s);
      const w = await adminListWithdrawals(statusFilter || undefined);
      setWithdrawals(Array.isArray(w) ? w : []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load payouts.");
    }
  }, [statusFilter, onToast]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!settings) return;
    const next = !settings.tips_enabled;
    if (!window.confirm(
      next
        ? "Activate creator payouts? This will instantly reveal the Tip button on every player card AND the Wallet page in the navbar."
        : "Deactivate creator payouts? Tip buttons will disappear immediately. Existing wallet balances stay intact."
    )) return;
    setBusy("toggle");
    try {
      const s = await adminTogglePayouts(next);
      setSettings(s);
      // Refresh global feature flags so the admin's own browser sees
      // the navbar Wallet link appear/disappear without a reload.
      await refresh?.();
      onToast?.(next ? "✅ Creator payouts ACTIVATED" : "Creator payouts deactivated.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't toggle.");
    } finally { setBusy(""); }
  };

  const applySplit = async () => {
    if (!splitUid.trim()) { onToast?.("Enter a user_id first."); return; }
    setBusy("split");
    try {
      await adminSetUserSplit(splitUid.trim(), parseFloat(splitPct));
      onToast?.(`Platform fee set to ${splitPct}% for ${splitUid.trim()}.`);
      setSplitUid("");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't set split.");
    } finally { setBusy(""); }
  };

  const decide = async (wid, decision) => {
    const note = window.prompt(
      `Add an admin note for "${decision}" (optional):`,
      "",
    );
    if (note === null) return;
    setBusy(`wd-${wid}`);
    try {
      await adminDecideWithdrawal(wid, decision, note || "");
      onToast?.(`Withdrawal ${decision}.`);
      load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't decide.");
    } finally { setBusy(""); }
  };

  if (!settings) {
    return <p className="text-[12px] text-white/40 p-4">Loading payouts…</p>;
  }

  return (
    <div className="space-y-6" data-testid="admin-payouts-section">
      {/* Master toggle */}
      <div className="rounded-2xl p-5 border border-[#14b8a6]/40 bg-gradient-to-br from-[#0a1f1c] via-[#0b2823] to-[#0a1f1c]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold mb-1">
              <i className="fas fa-coins mr-1" />CREATOR PAYOUTS
            </p>
            <h2 className="text-xl font-black text-white">
              {settings.tips_enabled ? "ACTIVE" : "DISABLED"}
            </h2>
            <p className="text-[11px] text-white/55 mt-1 max-w-md">
              When active, every player card shows a Tip button, paid tips credit the
              recipient's wallet (net of fee), and players can request withdrawals.
              When disabled the entire surface is hidden — no tips can be sent.
            </p>
          </div>
          <button
            type="button"
            onClick={toggle}
            disabled={busy === "toggle"}
            data-testid="admin-payouts-toggle"
            className={`text-[11px] tracking-widest px-5 py-2.5 rounded-xl font-bold ${
              settings.tips_enabled
                ? "bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30"
                : "bg-[#14b8a6] text-black hover:brightness-110"
            } disabled:opacity-50`}
          >
            {busy === "toggle" ? "…" : settings.tips_enabled ? "DEACTIVATE" : "ACTIVATE"}
          </button>
        </div>
      </div>

      {/* Per-user override */}
      <div className="rounded-2xl p-5 border border-white/10 bg-black/30">
        <p className="text-[10px] tracking-[3px] font-bold text-white/70 mb-2">
          <i className="fas fa-user-tag mr-1" />PER-USER PLATFORM FEE %
        </p>
        <p className="text-[11px] text-white/40 mb-3 max-w-md">
          Override the default 10% house take for a specific user. Range: 0-50%.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="user_id (e.g. user_abc123)"
            value={splitUid}
            onChange={(e) => setSplitUid(e.target.value)}
            data-testid="admin-payouts-split-uid"
            className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
          />
          <input
            type="number"
            min="0"
            max="50"
            step="0.5"
            value={splitPct}
            onChange={(e) => setSplitPct(e.target.value)}
            data-testid="admin-payouts-split-pct"
            className="w-24 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
          />
          <button
            type="button"
            onClick={applySplit}
            disabled={busy === "split"}
            data-testid="admin-payouts-split-apply"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-50"
          >
            {busy === "split" ? "…" : "APPLY"}
          </button>
        </div>
      </div>

      {/* Withdrawal queue */}
      <div className="rounded-2xl p-5 border border-white/10 bg-black/30" data-testid="admin-payouts-withdrawals">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[3px] font-bold text-white/70">
            <i className="fas fa-arrow-right-from-bracket mr-1" />WITHDRAWAL QUEUE
          </p>
          <div className="flex gap-1">
            {["requested", "approved", "paid", "rejected"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                data-testid={`admin-wd-filter-${s}`}
                className={`text-[10px] tracking-widest px-2.5 py-1 rounded-lg ${
                  statusFilter === s ? "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40" : "bg-black/30 text-white/50 border border-white/10"
                }`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {withdrawals.length === 0 ? (
          <p className="text-[11px] text-white/40">No {statusFilter} withdrawals.</p>
        ) : (
          <ul className="space-y-2">
            {withdrawals.map((w) => (
              <li key={w.withdrawal_id}
                  data-testid={`admin-wd-row-${w.withdrawal_id}`}
                  className="p-3 rounded-xl bg-black/40 border border-white/10">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-widest text-white/40">{w.withdrawal_id}</span>
                    <span className="text-[10px] uppercase text-white/50">{w.method}</span>
                  </div>
                  <span className="font-bold text-white">${Number(w.gross_amount_usd).toFixed(2)}</span>
                </div>
                <p className="text-[12px] text-white/70 mb-2 break-all">
                  user: <code className="text-[#14b8a6]">{w.user_id}</code> · dest: <code className="text-white/90">{w.destination}</code>
                </p>
                <p className="text-[11px] text-white/40 mb-2">
                  surcharge ${Number(w.surcharge_usd).toFixed(2)} · net to player ${Number(w.net_payout_usd).toFixed(2)}
                </p>
                {w.admin_note && <p className="text-[11px] text-amber-300/80 mb-2">note: {w.admin_note}</p>}
                {w.status === "requested" && (
                  <div className="flex gap-2">
                    <button type="button"
                            onClick={() => decide(w.withdrawal_id, "approved")}
                            disabled={busy === `wd-${w.withdrawal_id}`}
                            data-testid={`admin-wd-approve-${w.withdrawal_id}`}
                            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200">APPROVE</button>
                    <button type="button"
                            onClick={() => decide(w.withdrawal_id, "paid")}
                            disabled={busy === `wd-${w.withdrawal_id}`}
                            data-testid={`admin-wd-paid-${w.withdrawal_id}`}
                            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/20 border border-[#14b8a6]/50 text-[#14b8a6]">MARK PAID</button>
                    <button type="button"
                            onClick={() => decide(w.withdrawal_id, "rejected")}
                            disabled={busy === `wd-${w.withdrawal_id}`}
                            data-testid={`admin-wd-reject-${w.withdrawal_id}`}
                            className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200">REJECT (REFUND)</button>
                  </div>
                )}
                {w.status === "approved" && (
                  <button type="button"
                          onClick={() => decide(w.withdrawal_id, "paid")}
                          disabled={busy === `wd-${w.withdrawal_id}`}
                          data-testid={`admin-wd-paid-${w.withdrawal_id}`}
                          className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/20 border border-[#14b8a6]/50 text-[#14b8a6]">MARK PAID</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
